import { JsonProperty, Serializable } from 'ts-jackson'
import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'
import { Fromable } from '../model'

// ─── Enums ────────────────────────────────────────────────────────────────────

export type SessionRiskLevel = 'CRITICAL' | 'AT_RISK' | 'WATCH' | 'ON_TRACK' | 'COMPLETED'
export type PtStatusLevel = 'EXCELLENT' | 'NORMAL' | 'NEEDS_IMPROVEMENT' | 'CRITICAL'
export type SessionHistoryStatus = 'COMPLETED' | 'SCHEDULED' | 'NO_SHOW' | 'CANCELLED'

// ─── Filters / Params ─────────────────────────────────────────────────────────

export interface SessionUtilizationFilter {
  branchId?: number
}

export interface AtRiskQueryParams {
  branchId?: number
  ptId?: string
  riskLevel?: 'CRITICAL' | 'AT_RISK' | 'WATCH'
  package?: string
  sortField?:
    | 'followUpScore'
    | 'remainingSessions'
    | 'daysRemaining'
    | 'requiredBurnRate'
    | 'burnRateGap'
    | 'valueAtRisk'
    | 'daysSinceLastTrain'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

// ─── Summary ──────────────────────────────────────────────────────────────────

@Serializable()
export class SummaryAtRisk extends Fromable {
  @JsonProperty('sessionCount')
  sessionCount!: number

  @JsonProperty('totalValue')
  totalValue!: number
}

@Serializable()
export class SummaryUtilization extends Fromable {
  @JsonProperty('avgRate')
  avgRate!: number

  @JsonProperty('targetRate')
  targetRate!: number

  @JsonProperty('isBelowTarget')
  isBelowTarget!: boolean
}

@Serializable()
export class SummaryActivePackages extends Fromable {
  @JsonProperty('count')
  count!: number

  @JsonProperty('totalValue')
  totalValue!: number
}

@Serializable()
export class SummaryRebooking extends Fromable {
  @JsonProperty('rate')
  rate!: number

  @JsonProperty('targetRate')
  targetRate!: number

  @JsonProperty('isBelowTarget')
  isBelowTarget!: boolean
}

@Serializable()
export class SessionUtilizationSummary extends Fromable {
  @JsonProperty({ path: 'atRisk', type: SummaryAtRisk })
  atRisk!: SummaryAtRisk

  @JsonProperty({ path: 'utilization', type: SummaryUtilization })
  utilization!: SummaryUtilization

  @JsonProperty({ path: 'activePackages', type: SummaryActivePackages })
  activePackages!: SummaryActivePackages

  @JsonProperty({ path: 'rebooking', type: SummaryRebooking })
  rebooking!: SummaryRebooking
}

// ─── Scatter ──────────────────────────────────────────────────────────────────

@Serializable()
export class ScatterPoint extends Fromable {
  @JsonProperty('memberId')
  memberId!: string

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('ptId')
  ptId!: string

  @JsonProperty('ptName')
  ptName!: string

  @JsonProperty('package')
  package!: string

  @JsonProperty('packagePrice')
  packagePrice!: number

  @JsonProperty('totalSessions')
  totalSessions!: number

  @JsonProperty('usedSessions')
  usedSessions!: number

  @JsonProperty('remainingSessions')
  remainingSessions!: number

  @DayjsProperty({ path: 'expiryDate' })
  expiryDate!: Dayjs

  @JsonProperty('daysRemaining')
  daysRemaining!: number

  @JsonProperty('timeElapsedPct')
  timeElapsedPct!: number

  @JsonProperty('sessionUsedPct')
  sessionUsedPct!: number

  @JsonProperty('riskLevel')
  riskLevel!: SessionRiskLevel

  @JsonProperty('valueAtRisk')
  valueAtRisk!: number
}

// ─── PT Performance ───────────────────────────────────────────────────────────

@Serializable()
export class PtPerformanceMember extends Fromable {
  @JsonProperty('memberId')
  memberId!: string

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('package')
  package!: string

  @JsonProperty('totalSessions')
  totalSessions!: number

  @JsonProperty('usedSessions')
  usedSessions!: number

  @JsonProperty('remainingSessions')
  remainingSessions!: number

  @JsonProperty('daysRemaining')
  daysRemaining!: number

  @JsonProperty('riskLevel')
  riskLevel!: SessionRiskLevel
}

@Serializable()
export class PtPerformance extends Fromable {
  @JsonProperty('ptId')
  ptId!: string

  @JsonProperty('ptName')
  ptName!: string

  @JsonProperty('activeMembers')
  activeMembers!: number

  @JsonProperty('totalSessions')
  totalSessions!: number

  @JsonProperty('usedSessions')
  usedSessions!: number

  @JsonProperty('utilizationRate')
  utilizationRate!: number

  @JsonProperty('atRiskSessions')
  atRiskSessions!: number

  @JsonProperty('rebookingRate')
  rebookingRate!: number

  @JsonProperty('noShowRate')
  noShowRate!: number

  @JsonProperty('avgSessionGapDays')
  avgSessionGapDays!: number

  @JsonProperty('ptStatus')
  ptStatus!: PtStatusLevel

  @JsonProperty({ path: 'members', elementType: PtPerformanceMember })
  members!: PtPerformanceMember[]
}

// ─── At-Risk List ─────────────────────────────────────────────────────────────

@Serializable()
export class AtRiskItem extends Fromable {
  @JsonProperty('memberId')
  memberId!: string

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('ptId')
  ptId!: string

  @JsonProperty('ptName')
  ptName!: string

  @JsonProperty('package')
  package!: string

  @JsonProperty('packagePrice')
  packagePrice!: number

  @JsonProperty('totalSessions')
  totalSessions!: number

  @JsonProperty('usedSessions')
  usedSessions!: number

  @JsonProperty('remainingSessions')
  remainingSessions!: number

  @DayjsProperty({ path: 'expiryDate' })
  expiryDate!: Dayjs

  @JsonProperty('daysRemaining')
  daysRemaining!: number

  @JsonProperty('requiredBurnRate')
  requiredBurnRate!: number

  @JsonProperty('currentBurnRate')
  currentBurnRate!: number

  @JsonProperty('burnRateGap')
  burnRateGap!: number

  @JsonProperty('valueAtRisk')
  valueAtRisk!: number

  @DayjsProperty({ path: 'lastTrainDate' })
  lastTrainDate!: Dayjs | null

  @JsonProperty('daysSinceLastTrain')
  daysSinceLastTrain!: number

  @JsonProperty('noShowCount')
  noShowCount!: number

  @JsonProperty('riskLevel')
  riskLevel!: SessionRiskLevel

  @JsonProperty('followUpScore')
  followUpScore!: number

  @JsonProperty('followUpPriority')
  followUpPriority!: number
}

@Serializable()
export class AtRiskPage extends Fromable {
  @JsonProperty('total')
  total!: number

  @JsonProperty('page')
  page!: number

  @JsonProperty('limit')
  limit!: number

  @JsonProperty({ path: 'items', elementType: AtRiskItem })
  items!: AtRiskItem[]
}

// ─── Member Detail ────────────────────────────────────────────────────────────

@Serializable()
export class MemberDetailMember extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('phone')
  phone!: string | null

  @JsonProperty('email')
  email!: string | null
}

@Serializable()
export class MemberDetailPt extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class MemberDetailPackage extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('totalSessions')
  totalSessions!: number

  @JsonProperty('usedSessions')
  usedSessions!: number

  @JsonProperty('remainingSessions')
  remainingSessions!: number

  @JsonProperty('packagePrice')
  packagePrice!: number

  @JsonProperty('pricePerSession')
  pricePerSession!: number

  @DayjsProperty({ path: 'startDate' })
  startDate!: Dayjs

  @DayjsProperty({ path: 'expiryDate' })
  expiryDate!: Dayjs

  @JsonProperty('daysRemaining')
  daysRemaining!: number

  @JsonProperty('valueAtRisk')
  valueAtRisk!: number
}

@Serializable()
export class MemberDetailBurnRate extends Fromable {
  @JsonProperty('current')
  current!: number

  @JsonProperty('required')
  required!: number

  @JsonProperty('gap')
  gap!: number

  @JsonProperty('riskLevel')
  riskLevel!: SessionRiskLevel
}

@Serializable()
export class MemberDetailUpcomingSession extends Fromable {
  @DayjsProperty({ path: 'scheduledAt' })
  scheduledAt!: Dayjs
}

@Serializable()
export class MemberDetailRebooking extends Fromable {
  @JsonProperty('hasUpcomingSession')
  hasUpcomingSession!: boolean

  @JsonProperty({ path: 'upcomingSession', type: MemberDetailUpcomingSession })
  upcomingSession!: MemberDetailUpcomingSession | null
}

@Serializable()
export class MemberDetailSessionHistory extends Fromable {
  @JsonProperty('sessionId')
  sessionId!: string

  @DayjsProperty({ path: 'scheduledAt' })
  scheduledAt!: Dayjs

  @DayjsProperty({ path: 'completedAt' })
  completedAt!: Dayjs | null

  @JsonProperty('type')
  type!: string

  @JsonProperty('status')
  status!: SessionHistoryStatus
}

@Serializable()
export class MemberDetail extends Fromable {
  @JsonProperty({ path: 'member', type: MemberDetailMember })
  member!: MemberDetailMember

  @JsonProperty({ path: 'pt', type: MemberDetailPt })
  pt!: MemberDetailPt

  @JsonProperty({ path: 'package', type: MemberDetailPackage })
  package!: MemberDetailPackage

  @JsonProperty({ path: 'burnRate', type: MemberDetailBurnRate })
  burnRate!: MemberDetailBurnRate

  @JsonProperty({ path: 'rebooking', type: MemberDetailRebooking })
  rebooking!: MemberDetailRebooking

  @JsonProperty('weeklyFrequency')
  weeklyFrequency!: number[]

  @JsonProperty({ path: 'sessionHistory', elementType: MemberDetailSessionHistory })
  sessionHistory!: MemberDetailSessionHistory[]
}
